import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import backendAPI from '../../../utils/backendAPI'
import { getMerchantCheckoutContext } from '../../../utils/merchantContext'
import { stripeLocaleFromAppLang } from '../../../utils/stripeLocale'
import KlerkMerchantShell from '../../../components/KlerkMerchantShell.js'

function useMerchantContext(userInfo) {
	return useMemo(() => getMerchantCheckoutContext(userInfo), [userInfo])
}

function formatBillingDate(iso) {
	if (!iso) return '—'
	try {
		return new Date(iso).toLocaleString(undefined, {
			dateStyle: 'medium',
			timeStyle: 'short',
		})
	} catch {
		return '—'
	}
}

function humanizePlanLabel(planValue, intervalValue, t) {
	const raw = String(planValue || '').trim()
	const interval = String(intervalValue || '').trim().toLowerCase()
	const intervalLabel = interval ? ` (${interval})` : ''
	if (!raw) return t('billing.defaultPlan')
	if (!raw.startsWith('price_')) return raw
	return `${t('billing.klerkSubscription')}${intervalLabel}`
}

function BillingPage() {
	const [searchParams, setSearchParams] = useSearchParams()
	const [portalLoading, setPortalLoading] = useState(false)
	const [checkoutLoading, setCheckoutLoading] = useState(false)
	const [error, setError] = useState('')
	const [summaryError, setSummaryError] = useState('')
	const [summaryLoading, setSummaryLoading] = useState(true)
	const [summary, setSummary] = useState(null)
	const [checkoutBanner, setCheckoutBanner] = useState(null)
	const { t, i18n } = useTranslation()
	const { userInfo } = useSelector((state) => state.userLogin)
	const { merchantId, merchantEmail, businessName } = useMerchantContext(userInfo)
	const [resolvedMerchantId, setResolvedMerchantId] = useState(merchantId)
	const [merchantResolving, setMerchantResolving] = useState(!merchantId)
	const [billingEmail, setBillingEmail] = useState('')
	const effectiveEmail = merchantEmail || billingEmail.trim()
	const resolvedBusinessName =
		String(summary?.businessName || businessName || localStorage.getItem('business_name') || '')
			.trim() || t('billing.yourStore')

	const missingMerchant = !merchantResolving && !resolvedMerchantId
	const missingEmail = !effectiveEmail

	useEffect(() => {
		setResolvedMerchantId(merchantId)
		if (merchantId) setMerchantResolving(false)
	}, [merchantId])

	useEffect(() => {
		let cancelled = false
		if (resolvedMerchantId) {
			setMerchantResolving(false)
			return () => {}
		}
		setMerchantResolving(true)
		;(async () => {
			try {
				const { data } = await backendAPI.get('/api/user-businesses/')
				const firstBusinessId = Array.isArray(data)
					? String(data[0]?.merchant_id || data[0]?.business_id || '').trim()
					: ''
				if (!cancelled && firstBusinessId) {
					localStorage.setItem('merchant_id', firstBusinessId)
					localStorage.setItem('business_id', firstBusinessId)
					setResolvedMerchantId(firstBusinessId)
				}
			} catch {
				// Billing summary may still resolve the store from your login on the server.
			} finally {
				if (!cancelled) setMerchantResolving(false)
			}
		})()
		return () => { cancelled = true }
	}, [resolvedMerchantId])

	const fetchSummary = useCallback(async () => {
		setSummaryLoading(true)
		setSummaryError('')
		try {
			const params = resolvedMerchantId ? { merchantId: resolvedMerchantId } : {}
			const { data } = await backendAPI.get('/api/stripe/billing-summary/', { params })
			if (data?.merchant_id && data.merchant_id !== resolvedMerchantId) {
				localStorage.setItem('merchant_id', String(data.merchant_id))
				localStorage.setItem('business_id', String(data.merchant_id))
				setResolvedMerchantId(String(data.merchant_id))
			}
			setSummary(data)
		} catch (err) {
			const msg =
				err.response?.data?.error ||
				err.response?.data?.detail ||
				err.message ||
				'Could not load payment details'
			setSummaryError(msg)
			setSummary(null)
		} finally {
			setSummaryLoading(false)
		}
	}, [resolvedMerchantId])

	useEffect(() => {
		fetchSummary()
	}, [fetchSummary])

	useEffect(() => {
		const flag = searchParams.get('checkout')
		if (!flag) return
		if (flag === 'success') {
			setCheckoutBanner({ type: 'success', text: t('billing.checkoutSuccess') })
		}
		if (flag === 'warn') {
			setCheckoutBanner({ type: 'warning', text: t('billing.checkoutWarn') })
		}
		fetchSummary()
		setSearchParams({}, { replace: true })
	}, [searchParams, setSearchParams, fetchSummary, t])

	useEffect(() => {
		if (searchParams.get('billing') !== 'refresh') return
		fetchSummary()
		setSearchParams({}, { replace: true })
	}, [searchParams, setSearchParams, fetchSummary])

	const handleManageBilling = async () => {
		if (!resolvedMerchantId) {
			setError('Please complete business setup before managing billing.')
			return
		}
		setPortalLoading(true)
		setError('')
		try {
			const body = { merchantId: resolvedMerchantId }
			const loc = stripeLocaleFromAppLang(i18n.language)
			if (loc) body.locale = loc
			const { data } = await backendAPI.post('/api/stripe/create-portal-session/', body)
			if (data?.url) {
				window.location.href = data.url
				return
			}
			throw new Error('No portal URL returned')
		} catch (err) {
			const msg =
				err.response?.data?.error ||
				err.response?.data?.detail ||
				err.message ||
				'Could not open customer portal'
			if (/STRIPE_SECRET_KEY is not configured/i.test(String(msg))) {
				setError('Billing is temporarily unavailable. Please try again later or contact support.')
				setPortalLoading(false)
				return
			}
			if (
				/No Stripe customer on file/i.test(String(msg)) &&
				effectiveEmail &&
				resolvedMerchantId
			) {
				try {
					setCheckoutLoading(true)
					const { data } = await backendAPI.post('/api/stripe/create-checkout-session/', {
						merchantId: resolvedMerchantId,
						merchantEmail: effectiveEmail,
						businessName: resolvedBusinessName,
					})
					if (data?.url) {
						window.location.href = data.url
						return
					}
					setError('Could not start checkout. Please try again.')
				} catch (checkoutErr) {
					const checkoutMsg =
						checkoutErr.response?.data?.error ||
						checkoutErr.response?.data?.detail ||
						checkoutErr.message ||
						'Could not start checkout. Please try again.'
					setError(checkoutMsg)
				} finally {
					setCheckoutLoading(false)
					setPortalLoading(false)
				}
				return
			}
			setError(msg)
			setPortalLoading(false)
		}
	}

	const isTrial = summary?.subscriptionStatus === 'trialing'

	const statusDisplay = (() => {
		if (!summary) return '—'
		if (summary.hasSubscription) {
			if (summary.subscriptionStatus === 'trialing') return t('billing.statusTrial')
			if (summary.subscriptionStatus === 'past_due') return t('billing.statusPastDue')
			return t('billing.statusActive')
		}
		if (summary.subscriptionStatus === 'canceled') return t('billing.statusCanceled')
		if (summary.subscriptionStatus === 'unpaid') return t('billing.statusUnpaid')
		return '—'
	})()

	const currentPlanDisplay = !summary?.hasSubscription
		? t('billing.noSubscription')
		: [
			humanizePlanLabel(summary.currentPlan, summary?.currentPlanInterval, t),
			isTrial ? t('billing.trialLabel') : '',
		].filter(Boolean).join(' ')

	const nextChargeLabel = isTrial
		? t('billing.firstCharge')
		: t('billing.nextBillingDate')

	const nextBillingDateDisplay =
		summary?.hasSubscription && summary?.nextBillingDate
			? formatBillingDate(summary.nextBillingDate)
			: '—'

	return (
		<KlerkMerchantShell
			pageTitle={t('sidebar.billing')}
			storeLabel={summary?.businessName || businessName || t('billing.yourStore')}
		>
			<div className="container-fluid px-0" style={{ maxWidth: '700px' }}>
				<h1 className="h3 mb-2">{t('sidebar.billing')}</h1>
				<p className="text-muted mb-4">{t('billing.subtitle')}</p>

				{checkoutBanner && (
					<div
						className={`alert ${checkoutBanner.type === 'success' ? 'alert-success' : 'alert-warning'}`}
						role="status"
					>
						{checkoutBanner.text}
					</div>
				)}

				{merchantResolving && (
					<p className="text-muted small mb-3" role="status">
						{t('billing.loadingStore')}
					</p>
				)}

				{missingMerchant && (
					<div className="alert alert-info" role="status">
						<p className="mb-2 mb-md-3">{t('billing.finishSetup')}</p>
						<Link to="/sign-up/" className="btn btn-sm btn-primary">
							{t('billing.completeSetup')}
						</Link>
					</div>
				)}

				{!missingMerchant && missingEmail && (
					<div className="alert alert-light border" role="status">
						<p className="mb-2">{t('billing.enterEmail')}</p>
						<label className="form-label small mb-1" htmlFor="billing-fallback-email">
							{t('billing.emailLabel')}
						</label>
						<input
							id="billing-fallback-email"
							type="email"
							className="form-control form-control-sm"
							autoComplete="email"
							value={billingEmail}
							onChange={(e) => setBillingEmail(e.target.value)}
							placeholder={t('billing.emailPlaceholder')}
						/>
					</div>
				)}

				<div className="card" style={{ marginTop: '1.5rem' }}>
					<div className="card-body">
						<h2 className="h4 mb-3">{t('billing.yourSubscription')}</h2>

						{summaryError && (
							<p className="text-warning mb-3" role="alert">
								{summaryError}
							</p>
						)}

						<dl className="row mb-0">
							<dt className="col-sm-4">{t('billing.status')}</dt>
							<dd className="col-sm-8">{summaryLoading ? '…' : statusDisplay}</dd>

							<dt className="col-sm-4">{t('billing.currentPlan')}</dt>
							<dd className="col-sm-8">{summaryLoading ? '…' : currentPlanDisplay}</dd>

							<dt className="col-sm-4">{nextChargeLabel}</dt>
							<dd className="col-sm-8">{summaryLoading ? '…' : nextBillingDateDisplay}</dd>
						</dl>

						<button
							type="button"
							className="btn btn-primary mt-2"
							onClick={handleManageBilling}
							disabled={
								portalLoading ||
								checkoutLoading ||
								merchantResolving ||
								missingMerchant ||
								missingEmail
							}
						>
							{portalLoading
								? t('billing.opening')
								: checkoutLoading
									? t('billing.redirecting')
									: t('billing.manageBilling')}
						</button>

						<p className="text-muted small mt-2 mb-0">
							{t('billing.stripeHint')}
						</p>

						{error && (
							<p className="text-danger mt-3 mb-0" role="alert">
								{error}
							</p>
						)}
					</div>
				</div>
			</div>
		</KlerkMerchantShell>
	)
}

export default BillingPage